import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { AlertTriangle, ChevronDown, ChevronRight, ClipboardList, Filter, Plus, Search, Users, X } from 'lucide-react'
import { Badge, Button, Card } from '@/components/ui'
import { SearchableSelect } from '@/components/pedagogy/SearchableSelect'
import { TeacherStatusBadge } from '@/components/pedagogy/TeacherStatusBadge'
import { PedagogyAccessDeniedCard, PedagogyAccessErrorCard } from '@/components/pedagogy/PedagogyAccessStates'
import { usePedagogyAccess } from '@/hooks/usePedagogyAccess'
import {
  listFranchiseTeachersPage,
  listTeacherRegistryReferenceData,
  type FranchiseTeacherListItem,
  type FranchiseFormerPartnerAttention,
  type FranchiseTeacherStatus,
  type TeacherAttentionKind,
  type TeacherRegistryReferenceData,
  type TeacherTrainingFilter,
  type TeacherVideoFilter,
} from '@/services/franchiseTeacherService'
import { getTeacherInlineAttention, teacherAttentionClass } from '@/lib/teacherAttention'
import { getLegacyTrainerReviewMatchCounts } from '@/services/legacyTrainerReviewMatchService'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import { listPedagogySpecialists } from '@/services/pedagogySpecialistService'
import type { PedagogySpecialistListItem } from '@/types'

const PAGE_SIZE = 500
const employmentStatuses: FranchiseTeacherStatus[] = ['working', 'training', 'candidate', 'dismissed']
const attentionKinds: TeacherAttentionKind[] = ['attestation', 'contract', 'video', 'assignment', 'training', 'quality', 'review']

const attentionLabels: Record<TeacherAttentionKind, string> = {
  attestation: 'Аттестации',
  contract: 'Договоры',
  video: 'Видеовизитки',
  assignment: 'Привязки',
  training: 'Срок обучения',
  quality: 'Качество',
  review: 'Нет отсмотров',
}

type CityTeacherEntry = {
  key: string
  teacher: FranchiseTeacherListItem
  cityId: number
  cityName: string
  partnerNumber: number
  partnerName: string
  isSynthetic: boolean
}

type CityTeacherGroup = {
  key: string
  cityId: number
  cityName: string
  partnerNumber: number
  partnerName: string
  isSynthetic: boolean
  items: CityTeacherEntry[]
  uniqueTeachersCount: number
  activeTrainingCount: number
  attentionTeachersCount: number
  formerPartnerAttention: FranchiseFormerPartnerAttention | null
}

type AttentionSummary = {
  shortText: string
  title: string
  tone: 'error' | 'warning' | 'neutral'
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const date = new Date(`${value.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('ru-RU').format(date)
}

function isOverdue(value: string | null): boolean {
  if (!value) return false
  const date = new Date(`${value.slice(0, 10)}T23:59:59`)
  return !Number.isNaN(date.getTime()) && date.getTime() < Date.now()
}

function pluralRu(value: number, one: string, few: string, many: string): string {
  const mod10 = Math.abs(value) % 10
  const mod100 = Math.abs(value) % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

function parseEmployment(value: string | null): 'all' | FranchiseTeacherStatus {
  if (value === 'all') return 'all'
  return value && employmentStatuses.includes(value as FranchiseTeacherStatus)
    ? value as FranchiseTeacherStatus
    : 'working'
}

function parseTraining(value: string | null): TeacherTrainingFilter {
  return value === 'active' || value === 'inactive' ? value : 'all'
}

function parseVideo(value: string | null): TeacherVideoFilter {
  return value === 'missing' || value === 'pending' || value === 'approved' || value === 'rejected' ? value : 'all'
}

type AttentionFilter = 'all' | 'any' | TeacherAttentionKind

function parseAttention(value: string | null): AttentionFilter {
  if (value === 'any') return 'any'
  return value && attentionKinds.includes(value as TeacherAttentionKind) ? value as TeacherAttentionKind : 'all'
}

function parseOpenGroups(value: string | null): Set<string> {
  if (!value) return new Set()
  return new Set(
    value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean)
  )
}

function serializeOpenGroups(value: Set<string>): string {
  return Array.from(value).sort((left, right) => left.localeCompare(right, 'ru')).join(',')
}

function trainingText(teacher: FranchiseTeacherListItem): string {
  if (!teacher.currentTraining) return 'Нет'
  const parts = ['Идёт']
  if (teacher.currentTraining.startDate) parts.push(`с ${formatDate(teacher.currentTraining.startDate)}`)
  return parts.join(' ')
}

function contractText(teacher: FranchiseTeacherListItem): { text: string; overdue: boolean } {
  const nearest = teacher.activeContracts.find(contract => contract.expiresOn === teacher.nearestContractExpiry)
    ?? [...teacher.activeContracts].sort((left, right) => (left.expiresOn ?? '').localeCompare(right.expiresOn ?? ''))[0]
    ?? null
  if (!nearest) return { text: teacher.hasPassedFirstAttestation ? 'Нет договора' : '—', overdue: false }
  return { text: `до ${formatDate(nearest.expiresOn)}`, overdue: isOverdue(nearest.expiresOn) }
}

function attentionSummary(teacher: FranchiseTeacherListItem): AttentionSummary {
  if (teacher.status === 'dismissed') {
    return { shortText: 'Не требуется', title: 'Для уволенного педагога причины внимания не показываются', tone: 'neutral' }
  }
  const items = getTeacherInlineAttention(teacher)
  if (items.length === 0 && teacher.reviewsCount === 0) {
    return { shortText: 'Нет отсмотров', title: 'У педагога пока нет ни одного отсмотра', tone: 'warning' }
  }
  if (items.length === 0) return { shortText: 'Всё в порядке', title: 'Проблем нет', tone: 'neutral' }
  const title = items.map(item => item.label).join('\n')
  const worst = items.some(item => item.tone === 'error') ? 'error' : items.some(item => item.tone === 'warning') ? 'warning' : 'neutral'
  if (items.length === 1) return { shortText: items[0].label, title, tone: worst }
  return { shortText: `${items.length} проблемы`, title, tone: worst }
}

function activeFilterCount(values: {
  employmentStatus: 'all' | FranchiseTeacherStatus
  trainingState: TeacherTrainingFilter
  videoStatus: TeacherVideoFilter
  attentionKind: AttentionFilter
  specialistFilter: string
  partnerNumber: number | null
  cityId: number | null
  includeArchived: boolean
}): number {
  return [
    values.employmentStatus !== 'all',
    values.trainingState !== 'all',
    values.videoStatus !== 'all',
    values.attentionKind !== 'all',
    Boolean(values.specialistFilter),
    Boolean(values.partnerNumber),
    Boolean(values.cityId),
    values.includeArchived,
  ].filter(Boolean).length
}

function buildCityAssignmentEntries(teacher: FranchiseTeacherListItem): CityTeacherEntry[] {
  const seen = new Set<string>()
  const entries = teacher.assignments
    .filter(assignment => assignment.cityId > 0 && assignment.partnerNumber > 0)
    .map(assignment => ({
      key: `${teacher.teacherId}-${assignment.cityId}-${assignment.partnerNumber}`,
      teacher,
      cityId: assignment.cityId,
      cityName: assignment.cityName,
      partnerNumber: assignment.partnerNumber,
      partnerName: assignment.partnerName,
      isSynthetic: false,
    }))

  if (entries.length === 0) {
    return [{
      key: `${teacher.teacherId}-unassigned`,
      teacher,
      cityId: 0,
      cityName: 'Без активной связи',
      partnerNumber: 0,
      partnerName: teacher.status === 'dismissed' ? 'Уволенные педагоги' : 'Без привязки',
      isSynthetic: true,
    }]
  }

  return entries.filter(entry => {
    if (seen.has(entry.key)) return false
    seen.add(entry.key)
    return true
  })
}

function buildCityGroups(teachers: FranchiseTeacherListItem[]): CityTeacherGroup[] {
  const groups = new Map<string, CityTeacherGroup>()
  const attentionCache = new Map<string, AttentionSummary>()

  for (const teacher of teachers) {
    for (const entry of buildCityAssignmentEntries(teacher)) {
      const groupKey = `city-${entry.cityId}-partner-${entry.partnerNumber}`
      const formerPartnerAttention = entry.teacher.formerPartnerAttention.find(attention =>
        attention.cityId === entry.cityId && attention.partnerNumber === entry.partnerNumber
      ) ?? null
      const existing = groups.get(groupKey)
      if (existing) {
        existing.items.push(entry)
        if (!existing.formerPartnerAttention && formerPartnerAttention) {
          existing.formerPartnerAttention = formerPartnerAttention
        }
      } else {
        groups.set(groupKey, {
          key: groupKey,
          cityId: entry.cityId,
          cityName: entry.cityName,
          partnerNumber: entry.partnerNumber,
          partnerName: entry.partnerName,
          isSynthetic: entry.isSynthetic,
          items: [entry],
          uniqueTeachersCount: 0,
          activeTrainingCount: 0,
          attentionTeachersCount: 0,
          formerPartnerAttention,
        })
      }
    }

    if (!attentionCache.has(teacher.teacherId)) {
      attentionCache.set(teacher.teacherId, attentionSummary(teacher))
    }
  }

  return Array.from(groups.values())
    .filter(group => !group.formerPartnerAttention?.hideGroup)
    .map(group => {
      const teacherById = new Map<string, FranchiseTeacherListItem>()
      for (const item of group.items) {
        teacherById.set(item.teacher.teacherId, item.teacher)
      }

      return {
        ...group,
        items: [...group.items].sort((left, right) => {
          const teacherDiff = left.teacher.fullName.localeCompare(right.teacher.fullName, 'ru', { sensitivity: 'base' })
          if (teacherDiff !== 0) return teacherDiff
          return left.partnerName.localeCompare(right.partnerName, 'ru', { sensitivity: 'base' })
        }),
        uniqueTeachersCount: teacherById.size,
        activeTrainingCount: Array.from(teacherById.values()).filter(teacher => teacher.currentTraining != null).length,
        attentionTeachersCount: Array.from(teacherById.values()).filter(teacher => attentionCache.get(teacher.teacherId)?.tone !== 'neutral').length,
      }
    })
    .sort((left, right) => {
      if (left.isSynthetic !== right.isSynthetic) return left.isSynthetic ? 1 : -1
      const cityDiff = left.cityName.localeCompare(right.cityName, 'ru', { sensitivity: 'base' })
      if (cityDiff !== 0) return cityDiff
      return left.partnerName.localeCompare(right.partnerName, 'ru', { sensitivity: 'base' })
    })
}

function FormerPartnerAttentionNotice({ attention }: { attention: FranchiseFormerPartnerAttention }) {
  const partners = attention.activePartnerNames
    .map(item => item.partnerName || `Франчайзи №${item.partnerNumber}`)
    .join(', ')

  return (
    <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs leading-5 text-red-900">
      <div className="flex items-start gap-2">
        <AlertTriangle size={15} className="mt-0.5 shrink-0 text-red-600" aria-hidden="true" />
        <div className="min-w-0">
          <p><strong>Внимание.</strong> Франчайзи «{attention.partnerName}» больше не работает в городе {attention.cityName}.</p>
          <p>
            {attention.hasActiveStudios
              ? <>Студиями сейчас владеют: <strong>{partners || 'данные уточняются'}</strong>. Педагогов нужно вручную перевести к нужному франчайзи или завершить их работу.</>
              : <><strong>Действующих студий в городе нет.</strong> Педагогов нужно вручную перевести в другой город или завершить их работу.</>}
          </p>
        </div>
      </div>
    </div>
  )
}

export function PedagogyTeachersPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { loading: accessLoading, canViewPedagogySection, accessError, retryAccessCheck } = usePedagogyAccess()
  const [teachers, setTeachers] = useState<FranchiseTeacherListItem[]>([])
  const [uniqueTotal, setUniqueTotal] = useState(0)
  const [legacyPendingCount, setLegacyPendingCount] = useState(0)
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set())
  const [specialists, setSpecialists] = useState<PedagogySpecialistListItem[]>([])
  const [referenceData, setReferenceData] = useState<TeacherRegistryReferenceData | null>(null)
  const [loadingGroups, setLoadingGroups] = useState(true)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const [error, setError] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [search, setSearch] = useState(searchParams.get('q') ?? '')
  const [debouncedSearch, setDebouncedSearch] = useState(search.trim())
  const [beyondFiltersTotal, setBeyondFiltersTotal] = useState(0)

  const employmentStatus = parseEmployment(searchParams.get('status'))
  const trainingState = parseTraining(searchParams.get('training'))
  const videoStatus = parseVideo(searchParams.get('video'))
  const attentionKind = parseAttention(searchParams.get('attention'))
  const specialistFilter = searchParams.get('specialist') ?? ''
  const partnerNumber = Number(searchParams.get('partner')) || null
  const cityId = Number(searchParams.get('city')) || null
  const includeArchived = searchParams.get('archived') === '1'
  const openGroupsParam = searchParams.get('open')
  const filterKey = useMemo(
    () => JSON.stringify({
      search: debouncedSearch,
      employmentStatus,
      trainingState,
      videoStatus,
      attentionKind,
      specialistFilter,
      partnerNumber,
      cityId,
      includeArchived,
    }),
    [attentionKind, cityId, debouncedSearch, employmentStatus, includeArchived, partnerNumber, specialistFilter, trainingState, videoStatus]
  )
  const latestFilterKeyRef = useRef(filterKey)
  const openGroupsParamRef = useRef(openGroupsParam)

  useEffect(() => {
    latestFilterKeyRef.current = filterKey
  }, [filterKey])

  useEffect(() => {
    openGroupsParamRef.current = openGroupsParam
  }, [openGroupsParam])

  const setFilter = (key: string, value: string | null, defaultValue = 'all') => {
    const next = new URLSearchParams(searchParams)
    if (!value || value === defaultValue) next.delete(key)
    else next.set(key, value)
    setSearchParams(next, { replace: true })
  }

  const setCityFilter = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set('city', value)
    else next.delete('city')

    const currentPartner = next.get('partner')
    if (!value) {
      next.delete('partner')
    } else if (currentPartner) {
      const partnerStillAvailable = referenceData?.assignmentOptions.some(option =>
        String(option.cityId) === value && String(option.partnerNumber) === currentPartner
      )
      if (!partnerStillAvailable) next.delete('partner')
    }

    setSearchParams(next, { replace: true })
  }

  const resetFilters = () => {
    setSearch('')
    setSearchParams({}, { replace: true })
  }

  const loadTeachers = useCallback(async (requestFilterKey = filterKey) => {
    let offset = 0
    let total = 0
    const collected: FranchiseTeacherListItem[] = []

    do {
      const page = await listFranchiseTeachersPage({
        search: debouncedSearch,
        employmentStatus,
        trainingState,
        videoStatus,
        attentionKind: attentionKind === 'any' ? 'all' : attentionKind,
        quickFilter: attentionKind === 'any' ? 'attention' : 'all',
        specialistUserId: specialistFilter || null,
        partnerNumber,
        cityId,
        includeArchived,
        limit: PAGE_SIZE,
        offset,
      })

      if (latestFilterKeyRef.current !== requestFilterKey) return null

      collected.push(...page.items)
      total = page.total
      offset += page.items.length

      if (page.items.length === 0) break
    } while (offset < total)

    return { items: collected, total }
  }, [attentionKind, cityId, debouncedSearch, employmentStatus, filterKey, includeArchived, partnerNumber, specialistFilter, trainingState, videoStatus])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const normalized = search.trim()
      setDebouncedSearch(normalized)
      const next = new URLSearchParams(searchParams)
      if (normalized) next.set('q', normalized)
      else next.delete('q')
      if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true })
    }, 250)
    return () => window.clearTimeout(timeoutId)
  }, [search, searchParams, setSearchParams])

  useEffect(() => {
    if (accessLoading || !canViewPedagogySection) return
    let cancelled = false
    void getLegacyTrainerReviewMatchCounts()
      .then(value => { if (!cancelled) setLegacyPendingCount(value.pendingCount) })
      .catch(loadError => console.error('Failed to load legacy trainer review count:', loadError))
    return () => { cancelled = true }
  }, [accessLoading, canViewPedagogySection])

  useEffect(() => {
    if (accessLoading || !canViewPedagogySection) return
    let cancelled = false
    Promise.all([listPedagogySpecialists(), listTeacherRegistryReferenceData()])
      .then(([specialistRows, refs]) => {
        if (cancelled) return
        setSpecialists(specialistRows)
        setReferenceData(refs)
      })
      .catch(loadError => {
        if (!cancelled) console.error('Failed to load teacher registry filters:', loadError)
      })
    return () => { cancelled = true }
  }, [accessLoading, canViewPedagogySection])

  useEffect(() => {
    if (accessLoading || !canViewPedagogySection) return
    let cancelled = false
    setLoadingGroups(true)
    setError('')
    setTeachers([])

    loadTeachers(filterKey)
      .then(result => {
        if (cancelled || !result) return
        setTeachers(result.items)
        setUniqueTotal(result.total)
        const nextGroups = buildCityGroups(result.items)
        const requestedOpen = parseOpenGroups(openGroupsParamRef.current)
        const availableGroupKeys = new Set(nextGroups.map(group => group.key))
        const nextOpen = new Set(Array.from(requestedOpen).filter(groupKey => availableGroupKeys.has(groupKey)))
        const isSearching = Boolean(debouncedSearch || partnerNumber || cityId)
        setOpenGroups(isSearching ? new Set([...nextOpen, ...availableGroupKeys]) : nextOpen)
      })
      .catch(loadError => {
        if (cancelled) return
        console.error('Failed to load teachers:', loadError)
        setTeachers([])
        setUniqueTotal(0)
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить педагогов')
      })
      .finally(() => {
        if (cancelled) return
        setLoadingGroups(false)
        setHasLoadedOnce(true)
      })

    return () => { cancelled = true }
  }, [accessLoading, canViewPedagogySection, loadTeachers, filterKey, debouncedSearch, partnerNumber, cityId])

  const specialistOptions = useMemo(() => [
    { value: '', label: 'Все специалисты' },
    ...specialists.map(item => ({
      value: item.specialist_user_id,
      label: item.specialist_name || item.tg_username || `Специалист ${item.specialist_user_id.slice(0, 8)}`,
      description: `Франчайзи: ${item.partners_count}`,
      searchText: `${item.specialist_name ?? ''} ${item.tg_username ?? ''}`,
    })),
  ], [specialists])

  const partnerOptions = useMemo(() => {
    const availablePartners = cityId
      ? (referenceData?.assignmentOptions ?? [])
          .filter(option => option.cityId === cityId)
          .reduce<Map<number, string>>((map, option) => {
            map.set(option.partnerNumber, option.partnerName || `Франчайзи №${option.partnerNumber}`)
            return map
          }, new Map())
      : new Map<number, string>((referenceData?.partners ?? []).map(item => [item.partnerNumber, item.partnerName] as const))

    return [
      { value: '', label: 'Все франчайзи' },
      ...Array.from(availablePartners.entries())
        .map(([value, label]) => ({ value: String(value), label }))
        .sort((left, right) => left.label.localeCompare(right.label, 'ru')),
    ]
  }, [cityId, referenceData])

  const cityOptions = useMemo(() => [
    { value: '', label: 'Все города' },
    ...(referenceData?.cities ?? []).map(item => ({ value: String(item.cityId), label: item.cityName })),
  ], [referenceData])

  const groups = useMemo(() => buildCityGroups(teachers), [teachers])
  const hasSyntheticGroups = useMemo(() => groups.some(group => group.isSynthetic), [groups])

  // Фильтр «Рабочий статус» по умолчанию равен «Работает», а панель фильтров
  // свёрнута, поэтому поиск уволенного молча даёт ноль строк — карточка выглядит
  // несуществующей. Считаем, сколько совпадений есть без фильтров, и предлагаем их
  // показать, вместо «Измените поиск или фильтры».
  useEffect(() => {
    if (accessLoading || !canViewPedagogySection || loadingGroups) return
    if (!debouncedSearch || teachers.length > 0) {
      setBeyondFiltersTotal(0)
      return
    }

    let cancelled = false
    void listFranchiseTeachersPage({ search: debouncedSearch, includeArchived: true, limit: 1 })
      .then(page => { if (!cancelled) setBeyondFiltersTotal(page.total) })
      .catch(lookupError => {
        if (!cancelled) {
          console.error('Failed to count teachers beyond filters:', lookupError)
          setBeyondFiltersTotal(0)
        }
      })
    return () => { cancelled = true }
  }, [accessLoading, canViewPedagogySection, debouncedSearch, loadingGroups, teachers.length])

  const showAllMatches = () => {
    const next = new URLSearchParams()
    if (debouncedSearch) next.set('q', debouncedSearch)
    next.set('status', 'all')
    next.set('archived', '1')
    setSearchParams(next, { replace: true })
  }

  const filtersCount = activeFilterCount({
    employmentStatus,
    trainingState,
    videoStatus,
    attentionKind,
    specialistFilter,
    partnerNumber,
    cityId,
    includeArchived,
  })
  const hasFilters = Boolean(search || filtersCount > 0)

  const toggleGroup = (group: CityTeacherGroup) => {
    const isOpen = openGroups.has(group.key)
    const next = new Set(openGroups)
    if (isOpen) next.delete(group.key)
    else next.add(group.key)
    setOpenGroups(next)
    const nextParams = new URLSearchParams(searchParams)
    const serialized = serializeOpenGroups(next)
    if (serialized) nextParams.set('open', serialized)
    else nextParams.delete('open')
    setSearchParams(nextParams, { replace: true })
  }

  const openTeacher = (teacherId: string, partnerNumberValue?: number | null) => {
    const hasPartnerContext = Boolean(partnerNumberValue && partnerNumberValue > 0)
    navigate(
      hasPartnerContext ? `${pedagogyPaths.teacher(teacherId)}?partner=${partnerNumberValue}` : pedagogyPaths.teacher(teacherId),
      {
        state: {
          from: `${location.pathname}${location.search}`,
          ...(hasPartnerContext ? { partnerNumber: partnerNumberValue } : {}),
        },
      }
    )
  }

  if (accessError) return <PedagogyAccessErrorCard onRetry={retryAccessCheck} />
  if (!accessLoading && !canViewPedagogySection) return <PedagogyAccessDeniedCard />
  if (accessLoading || (loadingGroups && !hasLoadedOnce)) {
    return (
      <div className="space-y-4 pb-24">
        <Card><div className="py-10 text-center text-sm text-muted-foreground">Загружаем реестр педагогов…</div></Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-2xl font-bold tracking-tight">Педагоги</h1>
            <span className="text-sm font-medium text-muted-foreground">
              {uniqueTotal} {pluralRu(uniqueTotal, 'уникальный педагог', 'уникальных педагога', 'уникальных педагогов')}
              {hasFilters && !loadingGroups && groups.length > 0 && (
                <> · в {groups.length} {pluralRu(groups.length, hasSyntheticGroups ? 'группе' : 'связке', hasSyntheticGroups ? 'группах' : 'связках', hasSyntheticGroups ? 'группах' : 'связках')}{hasSyntheticGroups ? '' : ' город-франчайзи'}</>
              )}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="whitespace-nowrap"
            onClick={() => navigate(pedagogyPaths.legacyReviewMatching, { state: { from: `${location.pathname}${location.search}` } })}
          >
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><ClipboardList size={16} /> Непривязанные отсмотры · {legacyPendingCount}</span>
          </Button>
          <Button size="sm" className="whitespace-nowrap" onClick={() => navigate(pedagogyPaths.teacherNew)}>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Plus size={16} /> Добавить педагога</span>
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border border-error/30 bg-error/5">
          <div className="flex items-start gap-2 text-sm text-error"><AlertTriangle size={18} className="mt-0.5 shrink-0" /><span>{error}</span></div>
        </Card>
      )}

      <Card className="border border-border shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <label className="relative block min-w-0 flex-1">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Поиск по ФИО, телефону, email, Telegram, франчайзи или городу" className="h-10 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </label>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setFiltersOpen(value => !value)} className="h-10 whitespace-nowrap">
              <Filter size={16} className="mr-1.5 inline" />
              Фильтры{filtersCount > 0 ? ` · ${filtersCount}` : ''}
            </Button>
            {hasFilters && (
              <button type="button" className="h-10 whitespace-nowrap rounded-lg px-2 text-sm font-medium text-primary hover:bg-primary/10" onClick={resetFilters}>
                Сбросить
              </button>
            )}
          </div>
        </div>

        {filtersOpen && (
          <div className="mt-3 rounded-lg border border-border bg-muted/20 p-3">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="text-sm font-semibold">Фильтры</div>
              <button type="button" className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={() => setFiltersOpen(false)} aria-label="Закрыть фильтры">
                <X size={16} />
              </button>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <label>
                <div className="mb-1 text-xs font-medium text-muted-foreground">Рабочий статус</div>
                <select className="input w-full" value={employmentStatus} onChange={event => setFilter('status', event.target.value, 'working')}>
                  <option value="all">Все</option>
                  <option value="working">Работает</option>
                  <option value="training">До допуска к работе</option>
                  <option value="candidate">Кандидат</option>
                  <option value="dismissed">Уволен</option>
                </select>
              </label>
              <label>
                <div className="mb-1 text-xs font-medium text-muted-foreground">Обучение</div>
                <select className="input w-full" value={trainingState} onChange={event => setFilter('training', event.target.value)}>
                  <option value="all">Любое</option>
                  <option value="active">Идёт сейчас</option>
                  <option value="inactive">Не идёт</option>
                </select>
              </label>
              <label>
                <div className="mb-1 text-xs font-medium text-muted-foreground">Видеовизитка</div>
                <select className="input w-full" value={videoStatus} onChange={event => setFilter('video', event.target.value)}>
                  <option value="all">Любая</option>
                  <option value="missing">Нет</option>
                  <option value="pending">Ждёт оценки</option>
                  <option value="approved">Допущен</option>
                  <option value="rejected">Не допущен</option>
                </select>
              </label>
              <label>
                <div className="mb-1 text-xs font-medium text-muted-foreground">Требует внимания</div>
                <select className="input w-full" value={attentionKind} onChange={event => setFilter('attention', event.target.value)}>
                  <option value="all">Не важно</option>
                  <option value="any">Любая причина</option>
                  {attentionKinds.map(kind => <option key={kind} value={kind}>{attentionLabels[kind]}</option>)}
                </select>
              </label>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <SearchableSelect value={specialistFilter} options={specialistOptions} onChange={value => setFilter('specialist', value, '')} placeholder="Все специалисты" searchPlaceholder="Найти специалиста" />
              <SearchableSelect value={cityId ? String(cityId) : ''} options={cityOptions} onChange={setCityFilter} placeholder="Все города" searchPlaceholder="Найти город" />
              <SearchableSelect value={partnerNumber ? String(partnerNumber) : ''} options={partnerOptions} onChange={value => setFilter('partner', value, '')} placeholder="Все франчайзи" searchPlaceholder="Найти франчайзи" disabled={!cityId} />
            </div>
            <label className="mt-3 flex w-fit cursor-pointer items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={includeArchived} onChange={event => setFilter('archived', event.target.checked ? '1' : null, '')} />
              Показать архивные карточки
            </label>
          </div>
        )}
      </Card>

      {loadingGroups ? (
        <div className="overflow-hidden rounded-lg border border-border bg-card" aria-busy="true" aria-label="Обновляем реестр педагогов">
          <div className="divide-y divide-border">
            {[0, 1, 2, 3, 4].map(row => (
              <div key={row} className="flex min-h-[56px] items-center gap-3 px-3 py-2 md:px-4">
                <div className="h-4 w-4 shrink-0 animate-pulse rounded bg-muted" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-1/2 animate-pulse rounded bg-muted/70" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <div className="py-10 text-center">
            <Users size={32} className="mx-auto text-muted-foreground" />
            <div className="mt-3 font-medium">Педагоги не найдены</div>
            {beyondFiltersTotal > 0 ? (
              <>
                <div className="mt-1 text-sm text-muted-foreground">
                  По запросу «{debouncedSearch}» {beyondFiltersTotal === 1 ? 'есть' : 'есть'} {beyondFiltersTotal}{' '}
                  {pluralRu(beyondFiltersTotal, 'совпадение', 'совпадения', 'совпадений')} вне текущих фильтров —
                  например, уволенные: их скрывает фильтр «Рабочий статус».
                </div>
                <Button type="button" size="sm" variant="outline" className="mt-3" onClick={showAllMatches}>
                  Показать все совпадения
                </Button>
              </>
            ) : (
              <div className="mt-1 text-sm text-muted-foreground">Измените поиск или фильтры.</div>
            )}
          </div>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="divide-y divide-border">
            {groups.map(group => {
              const isOpen = openGroups.has(group.key)
              return (
                <section key={group.key}>
                  <button type="button" onClick={() => toggleGroup(group)} className="flex min-h-[56px] w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-muted/50 md:px-4">
                    {isOpen ? <ChevronDown size={17} className="shrink-0 text-muted-foreground" /> : <ChevronRight size={17} className="shrink-0 text-muted-foreground" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-semibold">{group.cityName}</span>
                        <span className="inline-flex shrink-0 items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium leading-none text-muted-foreground">
                          {group.partnerName}
                        </span>
                        {group.formerPartnerAttention && (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold leading-none text-red-700">
                            <AlertTriangle size={13} aria-hidden="true" />
                            Внимание
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                        <span>{group.uniqueTeachersCount} {pluralRu(group.uniqueTeachersCount, 'педагог', 'педагога', 'педагогов')}</span>
                        <span>{group.activeTrainingCount} в обучении</span>
                        {group.isSynthetic && <span>Нет действующей привязки</span>}
                        {group.attentionTeachersCount > 0 ? (
                          <span className="font-medium text-amber-700">⚠ {group.attentionTeachersCount} требуют внимания</span>
                        ) : (
                          <span>Нет отмеченных причин внимания</span>
                        )}
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-border bg-background">
                      {group.formerPartnerAttention && (
                        <div className="border-b border-red-100 bg-red-50/30 px-3 pb-3 md:px-4 md:pl-[52px]">
                          <FormerPartnerAttentionNotice attention={group.formerPartnerAttention} />
                        </div>
                      )}
                      <div className="hidden grid-cols-[minmax(220px,1.5fr)_120px_130px_100px_130px_130px_minmax(160px,1fr)] gap-3 border-b border-border bg-muted/30 px-4 py-2 text-xs font-medium text-muted-foreground xl:grid">
                        <div>Педагог</div>
                        <div>Рабочий статус</div>
                        <div>Обучение</div>
                        <div>Качество</div>
                        <div>Последний отсмотр</div>
                        <div>Договор</div>
                        <div>Требует внимания</div>
                      </div>
                      <div className="divide-y divide-border">
                        {group.items.map(entry => {
                          const contract = contractText(entry.teacher)
                          const attention = attentionSummary(entry.teacher)
                          const qualityLow = entry.teacher.averageQualityPercent != null && entry.teacher.averageQualityPercent < 50
                          return (
                            <button key={entry.key} type="button" onClick={() => openTeacher(entry.teacher.teacherId, entry.isSynthetic ? null : entry.partnerNumber)} className="block w-full px-3 py-2 pl-11 text-left transition-colors hover:bg-accent/40 md:px-4 md:pl-[52px] xl:grid xl:min-h-[48px] xl:grid-cols-[minmax(220px,1.5fr)_120px_130px_100px_130px_130px_minmax(160px,1fr)] xl:items-center xl:gap-3 xl:py-0">
                              <div className="min-w-0">
                                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                  <div className="min-w-0 truncate text-sm font-semibold">{entry.teacher.fullName}</div>
                                  {!entry.isSynthetic && entry.teacher.assignments.some(assignment => assignment.partnerNumber === entry.partnerNumber && assignment.cityId === entry.cityId && assignment.needsPedagogyReview) && (
                                    <Badge variant="info" className="shrink-0 px-2 py-0.5 text-[10px] leading-none">Связка создана франчайзи — проверьте актуальность</Badge>
                                  )}
                                  {!entry.isSynthetic && entry.teacher.assignments.some(assignment => assignment.partnerNumber === entry.partnerNumber && assignment.cityId === entry.cityId && assignment.isSenior) && (
                                    <Badge variant="warning" className="shrink-0 px-2 py-0.5 text-[10px] leading-none">Старший педагог</Badge>
                                  )}
                                </div>
                                <div className="mt-0.5 truncate text-xs text-muted-foreground xl:hidden">{entry.isSynthetic ? trainingText(entry.teacher) : `${entry.partnerName} · ${trainingText(entry.teacher)}`}</div>
                              </div>
                              <div className="mt-1 xl:mt-0"><TeacherStatusBadge status={entry.teacher.status} /></div>
                              <div className="hidden text-sm text-muted-foreground xl:block">{trainingText(entry.teacher)}</div>
                              <div className={`mt-1 text-sm font-medium xl:mt-0 ${qualityLow ? 'text-error' : 'text-foreground'}`}>{entry.teacher.averageQualityPercent == null ? '—' : `${entry.teacher.averageQualityPercent}%`}</div>
                              <div className="hidden text-sm text-muted-foreground xl:block">{formatDate(entry.teacher.latestReviewDate)}</div>
                              <div className={`hidden text-sm xl:block ${contract.overdue ? 'font-medium text-error' : 'text-muted-foreground'}`}>{contract.text}</div>
                              <div className="mt-1 min-w-0 xl:mt-0">
                                <span className={`inline-flex max-w-full items-center rounded-full border px-2 py-1 text-xs font-medium ${teacherAttentionClass(attention.tone)}`} title={attention.title}>
                                  <span className="truncate">{attention.shortText}</span>
                                </span>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </section>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
